import { keepPreviousData, useQuery } from "@tanstack/react-query";
import apiClient, { type ApiError } from "../client";

export const PORTFOLIO_BUILDER_QUERY_KEY = ["portfolio-builder"] as const;

/** Which file of portfolios: strategies that sell short as well as buy, or that only buy. */
export type PortfolioSide = "long-and-short" | "long-only";

/** A figure every portfolio carries, keyed as the run names it. */
export interface PortfolioFigureDto {
  key: string;
  label: string;
  unit: string;
}

/**
 * Instruments the customer can leave out together: one group per asset class. Bit i of `exclude` is
 * the group at index i.
 */
export interface PortfolioInstrumentGroupDto {
  /** Scr.Enums.AssetClass. Null for the symbols with no asset class set. */
  assetClass: number | null;
  symbols: string[];
}

/** One side of a strategy as the builder sells it, with its own figures from the backtest. */
export interface PortfolioStrategySideDto {
  publicStrategyId: string;
  /** The strategy as the rest of the portal knows it: the cart and subscriptions are keyed on this. */
  strategyId: string;
  monthlyPriceUsd: number;
  yearlyPriceUsd: number;
  figures: Record<string, number | null>;
}

export interface PortfolioStrategyDto {
  key: number;
  symbol: string;
  /** Null, with both sides, when it isn't a published strategy here. */
  name: string | null;
  /** Scr.Enums.RiskProfile. */
  riskProfile: number | null;
  group: number | null;
  longAndShort: PortfolioStrategySideDto | null;
  longOnly: PortfolioStrategySideDto | null;
}

export interface PortfolioBuilderDto {
  revisionId: string;
  backtest: { from: string; to: string };
  /** The first and last month-ends the growth line can use, as "2019-01". */
  firstMonth: string;
  lastMonth: string;
  /** How many questions there are to ask, not counting "Can your account sell short?". */
  questionCount: number;
  instrumentGroups: PortfolioInstrumentGroupDto[];
  figures: PortfolioFigureDto[];
  strategies: PortfolioStrategyDto[];
  /** Portfolios per side, then per size ("2" to "6"), then per exclude mask, 2^groups of them. */
  counts: Record<PortfolioSide, Record<string, number[]>>;
}

export interface PortfolioDotDto {
  ref: number;
  avgYearPct: number;
  realDdPct: number;
}

export interface PortfolioDotsBoundsDto {
  avgYearMin: number;
  avgYearMax: number;
  realDdMax: number;
}

export interface PortfolioDotsDto {
  revisionId: string;
  /** Over every portfolio in the group, not just the dots sent. Null when the group is empty. */
  bounds: PortfolioDotsBoundsDto | null;
  dots: PortfolioDotDto[];
}

export interface PortfolioDto {
  /** Not on a portfolio inside a match, which carries its own. */
  revisionId?: string;
  ref: number;
  size: number;
  strategies: number[];
  figures: Record<string, number | null>;
}

export type PortfolioAnswerValue = "a" | "b" | "skip";

/** One entry in the page's answers. `id` is a question's key, "check" or "refine". */
export interface PortfolioAnswer {
  id: string;
  v: PortfolioAnswerValue;
}

export interface PortfolioQuestionDto {
  key: string;
  text: string;
  answers: { title: string; line: string }[];
}

export type PortfolioNextDto =
  | { kind: "question"; question: PortfolioQuestionDto; skippable: boolean }
  | { kind: "check" }
  | { kind: "done"; left: number; exhausted: boolean };

export interface PortfolioPathStepDto {
  ref: number;
  avgYearPct: number;
  realDdPct: number;
  /** The answer that moved the match here. Null on the starting point. */
  label: string | null;
}

export interface PortfolioMatchDto {
  revisionId: string;
  pick: PortfolioDto;
  path: PortfolioPathStepDto[];
  next: PortfolioNextDto;
}

export interface PortfolioMatchRequest {
  side: PortfolioSide;
  size: number;
  exclude: number;
  history: PortfolioAnswer[];
}

/**
 * A match, with the side and group it was asked for: its refs are numbered within that side, and its
 * dot can only slide within one group.
 */
export interface PortfolioMatch extends PortfolioMatchDto {
  side: PortfolioSide;
  group: string;
}

export interface PortfolioGrowthMonthDto {
  month: string;
  value: number;
  drawdownPct: number;
}

export interface PortfolioGrowthDto {
  revisionId: string;
  start: string;
  amount: number;
  months: PortfolioGrowthMonthDto[];
  worth: number;
  gainPct: number;
  worstDrawdownPct: number;
}

/** The answer for one start month: the line, or that a strategy in it stopped trading before then. */
export type PortfolioGrowth =
  | { kind: "growth"; growth: PortfolioGrowthDto }
  | { kind: "startTooLate"; latestStart: string | null };

interface StartTooLateProblem {
  type?: string;
  latestStart?: string | null;
}

const START_TOO_LATE = "start-too-late";

/**
 * The amount the growth line is asked for. The simulator has no fees or minimum sizes, so a line is
 * exactly proportional to its amount, and one line per start month serves every amount typed.
 */
export const GROWTH_REQUEST_AMOUNT_USD = 10_000;

const MAX_RETRIES = 3;

// A 404 means the builder or its revision has gone, and a 400 won't change on a second asking, so
// only faults are tried again.
const retryFaults = (failureCount: number, error: Error) => {
  const status = (error as ApiError).status;
  return failureCount < MAX_RETRIES && !(status !== undefined && status >= 400 && status < 500);
};

/** The current revision. Null when the builder is off or nothing is current, which the API says with a 404. */
export function usePortfolioBuilder() {
  return useQuery<PortfolioBuilderDto | null>({
    queryKey: PORTFOLIO_BUILDER_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient.get<PortfolioBuilderDto>("/portfolio-builder");
        return res.data;
      } catch (error) {
        if ((error as ApiError).status === 404) {
          return null;
        }
        throw error;
      }
    },
    // A new revision is noticed on the next answer's response, which carries its id.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** The dots for one group. The same group always draws the same dots within a revision. */
export function usePortfolioDots(
  revisionId: string,
  side: PortfolioSide,
  size: number,
  exclude: number,
  enabled: boolean,
) {
  return useQuery<PortfolioDotsDto>({
    queryKey: [...PORTFOLIO_BUILDER_QUERY_KEY, revisionId, "dots", side, size, exclude],
    queryFn: async () => {
      const res = await apiClient.get<PortfolioDotsDto>("/portfolio-builder/dots", {
        params: { side, size, exclude },
      });
      return res.data;
    },
    enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: retryFaults,
  });
}

/** The match for the answers so far, the path it took and what to ask next. Null asks for nothing. */
export function usePortfolioMatch(revisionId: string, request: PortfolioMatchRequest | null) {
  return useQuery<PortfolioMatch>({
    queryKey: [...PORTFOLIO_BUILDER_QUERY_KEY, revisionId, "match", request],
    queryFn: async () => {
      const res = await apiClient.post<PortfolioMatchDto>("/portfolio-builder/match", request);
      const group = `${request!.side}|${request!.size}|${request!.exclude}`;
      return { ...res.data, side: request!.side, group };
    },
    enabled: request !== null,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: retryFaults,
  });
}

/** A portfolio the customer tapped, by its row in the revision. */
export function usePortfolio(revisionId: string, side: PortfolioSide, ref: number | null) {
  return useQuery<PortfolioDto>({
    queryKey: [...PORTFOLIO_BUILDER_QUERY_KEY, revisionId, "portfolios", side, ref],
    queryFn: async () => {
      const res = await apiClient.get<PortfolioDto>(
        `/portfolio-builder/portfolios/${side}/${ref}`,
      );
      return res.data;
    },
    enabled: ref !== null,
    staleTime: Infinity,
    retry: retryFaults,
  });
}

/** What GROWTH_REQUEST_AMOUNT_USD put into a portfolio at the end of `start` would have grown to. */
export function usePortfolioGrowth(
  revisionId: string,
  side: PortfolioSide,
  ref: number | undefined,
  start: string,
) {
  return useQuery<PortfolioGrowth>({
    queryKey: [...PORTFOLIO_BUILDER_QUERY_KEY, revisionId, "growth", side, ref, start],
    queryFn: async () => {
      // A 422 is an answer about the month chosen, not a fault, and its body says which month would work.
      const res = await apiClient.get<PortfolioGrowthDto | StartTooLateProblem>(
        `/portfolio-builder/portfolios/${side}/${ref}/growth`,
        {
          params: { amount: GROWTH_REQUEST_AMOUNT_USD, start },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 422,
        },
      );

      if (res.status !== 422) {
        return { kind: "growth", growth: res.data as PortfolioGrowthDto };
      }

      const problem = res.data as StartTooLateProblem;
      if (problem.type !== START_TOO_LATE) {
        throw new Error(`Growth refused for ${side} ${ref} from ${start}.`);
      }

      return { kind: "startTooLate", latestStart: problem.latestStart ?? null };
    },
    enabled: ref !== undefined,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: retryFaults,
  });
}
